// ─── Meta tile ────────────────────────────────────────────────────────
// A single secondary metric — overline label + h4 number + caption sub.
// Used for the three "new info" tiles below the hero (median time, aging,
// reject rate). Each is optionally clickable.

import { useNavigate } from "react-router-dom";
import { Box, Tooltip, Typography, useTheme } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { Info } from "lucide-react";
import { RavenIcon } from "@ravenappdev/raven-ui";

interface MetaTileProps {
  label: string;
  value: string;
  sub: string;
  to?: string;
  tone?: "neutral" | "warn";
  /** Tooltip content explaining how the metric is calculated. */
  info?: React.ReactNode;
}

export default function MetaTile({
  label,
  value,
  sub,
  to,
  // `tone` is currently visual-noise-only; reserved for future warn variants.
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  tone = "neutral",
  info,
}: MetaTileProps) {
  const navigate = useNavigate();
  const theme = useTheme();
  const interactive = !!to;
  const baseBg = "transparent";
  const hoverBg = interactive
    ? alpha(theme.palette.primary.main, 0.04)
    : "transparent";
  return (
    <Box
      role={interactive ? "button" : undefined}
      tabIndex={interactive ? 0 : undefined}
      onClick={interactive ? () => navigate(to!) : undefined}
      onKeyDown={
        interactive
          ? (e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                navigate(to!);
              }
            }
          : undefined
      }
      sx={{
        position: "relative",
        bgcolor: baseBg,
        border: "1px solid",
        borderColor: "divider",
        borderRadius: "12px",
        p: 1.75,
        display: "flex",
        flexDirection: "column",
        gap: 0.75,
        cursor: interactive ? "pointer" : "default",
        transition: "background-color .15s",
        "&:hover": interactive ? { bgcolor: hoverBg } : undefined,
        "&:focus-visible": interactive
          ? {
              outline: "2px solid",
              outlineColor: "primary.main",
              outlineOffset: 2,
            }
          : undefined,
      }}
    >
      <Typography
        variant="tableHeader"
        sx={{
          color: "text.primary",
          // Reserve space on the right so a long label doesn't run under
          // the absolutely-positioned info icon.
          pr: info ? 3 : 0,
        }}
      >
        {label}
      </Typography>
      {info && (
        <Tooltip
          title={info}
          arrow
          placement="top"
          enterTouchDelay={0}
          slotProps={{
            tooltip: {
              sx: { maxWidth: 280 },
            },
          }}
        >
          <Box
            component="span"
            role="button"
            tabIndex={0}
            aria-label={`How ${label} is calculated`}
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") e.stopPropagation();
            }}
            sx={{
              // Pin to the tile's top-right corner so the affordance is
              // anchored, regardless of how long the label wraps.
              position: "absolute",
              top: 8,
              right: 8,
              display: "inline-flex",
              alignItems: "center",
              color: "text.secondary",
              cursor: "help",
              lineHeight: 0,
              "&:hover, &:focus-visible": { color: "text.primary" },
              "&:focus-visible": {
                outline: "2px solid",
                outlineColor: "primary.main",
                outlineOffset: 2,
                borderRadius: "50%",
              },
            }}
          >
            <RavenIcon icon={Info} sx={{ fontSize: 14 }} />
          </Box>
        </Tooltip>
      )}
      <Typography variant="h4" color="text.primary" sx={{ lineHeight: 1.1 }}>
        {value}
      </Typography>
      <Typography variant="caption" color="text.secondary">
        {sub}
      </Typography>
    </Box>
  );
}
